import { useMemo } from 'react';

import { useQuery } from 'react-query';
import { GET_USERS_QUERY } from '@app/constants/query-api-configs';

import { UserApi } from '@app/domain/user/user.api';

import { useBasicQueryControls } from '@app/hooks/useBasicQueryControls.hook';

import { Typography } from '@mui/material';

import { Table, Button } from '@app/components';
import { useTableColumns } from './useTableColumns';
import {
  EPlanType,
  ECustomerGen,
  ERole,
  ESubscriptionAdminStatus,
  EUserStatus,
  ETeamMemberRole,
} from '@app/swagger-types';
import { renderFriendlyRole } from '@app/auth/auth.utils';
import pluralize from 'pluralize';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { oneOfEnum } from '@app/utils/yup.utils';

export const UsersTable: React.VFC<{
  role: ERole.ROLE_CUSTOMER;
}> = ({ role }) => {
  const {
    values: { page, sort = GET_USERS_QUERY.defaultSort, size },
    handlers: { handlePageChange, handleSortChange, handleSizeChange },
  } = useBasicQueryControls({
    initialValues: {
      page: 1,
      size: 10,
    },
    prefix: GET_USERS_QUERY.tablePrefix,
  });
  const [search] = useQueryParam(GET_USERS_QUERY.searchParam, withDefault(StringParam, ''));
  const [customerGen] = useQueryParam(GET_USERS_QUERY.customerGenParam, withDefault(StringParam, ''));
  const [planType] = useQueryParam(GET_USERS_QUERY.planTypeParam, withDefault(StringParam, ''));
  const [planStatus] = useQueryParam(GET_USERS_QUERY.planStatusParam, withDefault(StringParam, ''));
  const [userStatuses] = useQueryParam(GET_USERS_QUERY.userStatusesParam, withDefault(StringParam, ''));
  const [teamRoles] = useQueryParam(GET_USERS_QUERY.teamRoleParam, withDefault(StringParam, ''));

  const { data, isFetching } = useQuery(
    [
      GET_USERS_QUERY.name,
      { page, sort, size, role, search, customerGen, planType, planStatus, userStatuses, teamRoles },
    ],
    () => {
      if (role !== ERole.ROLE_CUSTOMER && role !== ERole.ROLE_ADMIN) {
        throw new Error('unhandled role case');
      }
      // TODO handle UserApi.getAdmins
      const method = role === ERole.ROLE_CUSTOMER ? UserApi.getCustomers : UserApi.getCustomers;
      // TODO auto validate params
      const validPriceGen = oneOfEnum(ECustomerGen).isValidSync(customerGen) ? customerGen : undefined;
      // TODO auto validate params
      const validType = oneOfEnum(EPlanType).isValidSync(planType) ? planType : undefined;
      // TODO auto validate params
      const validStatus = oneOfEnum(ESubscriptionAdminStatus).isValidSync(planStatus) ? planStatus : undefined;
      // TODO auto validate params
      const validUserStatuses = oneOfEnum(EUserStatus).isValidSync(userStatuses) ? userStatuses : undefined;
      // TODO auto validate params
      const validTeamRoles = oneOfEnum(ETeamMemberRole).isValidSync(teamRoles) ? teamRoles : undefined;
      return method({
        sort,
        page: Math.ceil((page || 1) - 1),
        size,
        term: search,
        customerGen: validPriceGen,
        planType: validType,
        planStatus: validStatus,
        userStatuses: validUserStatuses,
        teamRoles: validTeamRoles,
      });
    },
    { keepPreviousData: true, ...GET_USERS_QUERY.config }
  );

  const columns = useTableColumns();

  const leftContent = useMemo(() => {
    return (
      <div>
        <Typography variant="xl" className="font-bold">
          {pluralize(renderFriendlyRole(role))} Table ({data?.total})
        </Typography>
      </div>
    );
  }, [data?.total, role]);

  const rightContent = useMemo(() => {
    return (
      <div>
        <Button variant="contained" color="primary">
          Add New
        </Button>
      </div>
    );
  }, []);

  return (
    <Table
      cols={columns}
      tableData={data?.result ?? []}
      leftHeaderContent={leftContent}
      rightHeaderContent={rightContent}
      notFoundMessage="No users on file."
      currentPage={page}
      loading={isFetching}
      pageSize={size}
      onSizeChange={handleSizeChange}
      total={data?.total}
      onPagination={handlePageChange}
      currentSort={sort}
      onSortChanged={handleSortChange}
    />
  );
};
